import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import {
  QUALITY_RMAS_LIST_KEY,
  RMA_LED_BY,
  RMA_STATUSES,
  fetchQualityRmas,
  resolveQualityRmaArrivalScan,
  rmaLedByLabel,
  rmaStatusLabel,
  type QualityRma,
  type RmaLedBy,
  type RmaStatus,
} from "./rmaApi";

const HELP_TEXT = [
  "RMAs are return authorizations. Create one when a customer or end user asks to send material back.",
  "An RMA is the transit document for a return; it does not open an RCA by itself. Received is the last active RMA status. Open or link an RCA from the RMA when the devices need root cause analysis.",
].join(" ");

type RmaSortKey = "rmaNumber" | "status" | "customer" | "endUser" | "replacementSo" | "requestedAt" | "intakeCount";

function statusTone(status: RmaStatus): BadgeTone {
  switch (status) {
    case "closed":
      return "success";
    case "cancelled":
      return "neutral";
    case "received":
      return "warning";
    default:
      return "warning";
  }
}

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const isoDay = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(isoDay) ? isoDay : value;
}

function replacementSortValue(row: QualityRma): string {
  return row.salesOrders
    .map((salesOrder) => salesOrder.salesOrderNumber ?? String(salesOrder.salesOrderId))
    .join(" ");
}

const RMA_COLUMNS: HubDataTableColumn<QualityRma, RmaSortKey>[] = [
  {
    id: "rmaNumber",
    header: "RMA",
    sortKey: "rmaNumber",
    overflow: "nowrap",
    sortValue: (row) => row.rmaNumber,
    cell: (row) => (
      <Link className="font-medium text-sky-800 hover:underline" to={`/quality/rmas/${row.id}`}>
        {row.rmaNumber}
      </Link>
    ),
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => row.status,
    cell: (row) => (
      <div className="flex flex-wrap gap-1">
        <Badge tone={statusTone(row.status)}>{rmaStatusLabel(row.status)}</Badge>
        {row.receiveQuantityDiscrepancy ? <Badge tone="warning">Quantity discrepancy</Badge> : null}
      </div>
    ),
  },
  {
    id: "customer",
    header: "Customer",
    sortKey: "customer",
    sortValue: (row) => row.customerName ?? "",
    cell: (row) => row.customerName ?? EM_DASH,
  },
  {
    id: "endUser",
    header: "End user",
    sortKey: "endUser",
    sortValue: (row) => row.endUserName ?? "",
    cell: (row) => row.endUserName ?? EM_DASH,
  },
  {
    id: "replacementSo",
    header: "Replacement SO",
    sortKey: "replacementSo",
    sortValue: (row) => replacementSortValue(row),
    cell: (row) =>
      row.salesOrders.length === 0 ? (
        EM_DASH
      ) : (
        <div className="flex flex-col gap-0.5">
          {row.salesOrders.map((salesOrder) => (
            <SalesOrderDetailLink
              key={salesOrder.salesOrderId}
              salesOrderId={salesOrder.salesOrderId}
              salesOrderNumber={salesOrder.salesOrderNumber}
            />
          ))}
        </div>
      ),
  },
  {
    id: "intakeCount",
    header: "Intake",
    sortKey: "intakeCount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.intakeCount,
    cell: (row) => row.intakeCount,
  },
  {
    id: "requestedAt",
    header: "Requested",
    sortKey: "requestedAt",
    overflow: "nowrap",
    sortValue: (row) => row.requestedAt ?? "",
    cell: (row) => formatDate(row.requestedAt),
  },
];

function compareRmas(left: QualityRma, right: QualityRma, key: RmaSortKey, dir: SortDirection): number {
  const direction = dir === "asc" ? 1 : -1;
  switch (key) {
    case "rmaNumber":
      return left.rmaNumber.localeCompare(right.rmaNumber, undefined, { numeric: true }) * direction;
    case "status":
      return left.status.localeCompare(right.status) * direction;
    case "customer":
      return (left.customerName ?? "").localeCompare(right.customerName ?? "") * direction;
    case "endUser":
      return (left.endUserName ?? "").localeCompare(right.endUserName ?? "") * direction;
    case "replacementSo":
      return replacementSortValue(left).localeCompare(replacementSortValue(right), undefined, { numeric: true }) * direction;
    case "intakeCount":
      return (left.intakeCount - right.intakeCount) * direction;
    case "requestedAt":
    default:
      return (new Date(left.requestedAt ?? 0).getTime() - new Date(right.requestedAt ?? 0).getTime()) * direction;
  }
}

function arrivalScanHex(raw: string): string | null {
  const hex = raw.trim().toLowerCase().replace(/-/g, "");
  return /^[0-9a-f]{32}$/.test(hex) ? hex : null;
}

export function QualityRmasPage() {
  const navigate = useNavigate();
  const writesAllowed = useHubModuleWrite("quality");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<RmaStatus | "all">("all");
  const [ledByFilter, setLedByFilter] = useState<RmaLedBy | "all">("all");
  const [sortKey, setSortKey] = useState<RmaSortKey>("rmaNumber");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const scanHex = arrivalScanHex(search);
  const listQuery = useQuery({
    queryKey: [...QUALITY_RMAS_LIST_KEY, search, statusFilter, ledByFilter],
    queryFn: () =>
      fetchQualityRmas({
        search: search.trim() || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        ledBy: ledByFilter === "all" ? undefined : ledByFilter,
        limit: 200,
      }),
    enabled: scanHex == null,
  });
  const scanQuery = useQuery({
    queryKey: ["quality", "rmas", "resolve-scan", scanHex],
    queryFn: () => resolveQualityRmaArrivalScan(scanHex ?? ""),
    enabled: scanHex != null,
  });

  useEffect(() => {
    const rmaId = scanQuery.data?.rma?.id;
    if (rmaId == null) return;
    setSearch("");
    void navigate(`/quality/rmas/${rmaId}`);
  }, [navigate, scanQuery.data?.rma?.id]);

  const rows = useMemo(() => {
    const loaded = listQuery.data?.rows ?? [];
    return [...loaded].sort((left, right) => compareRmas(left, right, sortKey, sortDir));
  }, [listQuery.data?.rows, sortKey, sortDir]);

  const toggleSort = (key: RmaSortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "rmaNumber" || key === "requestedAt" || key === "intakeCount" ? "desc" : "asc");
  };

  const filtersActive = search.trim() !== "" || statusFilter !== "all" || ledByFilter !== "all";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>RMAs</PageTitleWithHelp>
        <div className="flex flex-wrap gap-2">
          {writesAllowed ? (
            <Button onClick={() => void navigate("/quality/rmas/new")}>
              <Plus className="mr-2 h-4 w-4" />
              New RMA
            </Button>
          ) : null}
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label htmlFor="quality-rma-search">Search RMAs</Label>
            <Input
              id="quality-rma-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by RMA number, replacement sales order, complaint, customer, or end user"
            />
          </div>
          <div>
            <Label htmlFor="quality-rma-led-by-filter">Led by</Label>
            <select
              id="quality-rma-led-by-filter"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={ledByFilter}
              onChange={(event) => setLedByFilter(event.target.value as RmaLedBy | "all")}
            >
              <option value="all">All</option>
              {RMA_LED_BY.map((ledBy) => (
                <option key={ledBy} value={ledBy}>
                  {rmaLedByLabel(ledBy)}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <p className="mb-2 text-xs font-medium text-slate-600">Status</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={statusFilter === "all" ? "primary" : "secondary"}
                onClick={() => setStatusFilter("all")}
              >
                All
              </Button>
              {RMA_STATUSES.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? "primary" : "secondary"}
                  onClick={() => setStatusFilter(status)}
                >
                  {rmaStatusLabel(status)}
                  {statusFilter === "all" && listQuery.data != null
                    ? ` (${listQuery.data.rows.filter((row) => row.status === status).length})`
                    : ""}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {scanHex != null ? (
        <Card>
          <CardHeader>
            <CardTitle>{scanQuery.isFetching ? "Opening RMA…" : "RMA tag"}</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-slate-600">
              {scanQuery.isError
                ? scanQuery.error instanceof Error
                  ? scanQuery.error.message
                  : "Could not look up this tag."
                : scanQuery.isFetching
                  ? "Looking up this tag…"
                  : "No RMA is linked to this tag."}
            </p>
          </CardBody>
        </Card>
      ) : (
        <HubDataTable
          title={
            listQuery.isLoading
              ? "Loading RMAs…"
              : `${rows.length} RMA${rows.length === 1 ? "" : "s"}`
          }
          columns={RMA_COLUMNS}
          rows={rows}
          rowKey={(row) => row.id}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleSort}
          loading={listQuery.isLoading}
          loadingMessage="Loading RMAs…"
          error={listQuery.isError ? (listQuery.error as Error).message : null}
          empty="No RMAs yet."
          isFiltered={filtersActive}
          filteredEmpty="No RMAs match these filters."
        />
      )}
    </div>
  );
}
